import {
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  Button,
  VStack,
  Flex,
  Box,
  Text,
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon } from '../../../assets/icons'
import { forwardRef, useState, useEffect } from 'react'

interface CustomNumericInputProps {
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  format?: (num: number) => string
  required?: boolean
}

export const CustomNumericInput = forwardRef<HTMLDivElement, CustomNumericInputProps>(
  ({ label, value, onChange, min = 0, max = 100, step = 1, format, required }, ref) => {
    // Track raw input separately from formatted display value
    const [inputValue, setInputValue] = useState<string>(value.toString())
    const [isFocused, setIsFocused] = useState(false)
    
    // Update internal state when external value changes
    useEffect(() => {
      if (!isFocused) {
        setInputValue(value.toString())
      }
    }, [value, isFocused])

    const increment = () => {
      const newValue = Math.min(value + step, max)
      onChange(newValue)
    }

    const decrement = () => {
      const newValue = Math.max(value - step, min)
      onChange(newValue)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      
      // Allow empty input for typing
      if (newValue === '') {
        setInputValue('')
        return
      }
      
      // Check if valid number
      const numericValue = Number(newValue)
      if (!isNaN(numericValue)) {
        setInputValue(newValue)
        // Only pass valid numbers to parent component
        if (numericValue >= min && numericValue <= max) {
          onChange(numericValue)
        }
      }
    }

    const handleBlur = () => {
      setIsFocused(false)
      
      // Handle empty or invalid values
      if (inputValue === '' || isNaN(Number(inputValue))) {
        setInputValue(value.toString())
        return
      }
      
      // Enforce min/max constraints
      let numericValue = Number(inputValue)
      if (numericValue < min) {
        numericValue = min
      } else if (numericValue > max) {
        numericValue = max
      }
      
      setInputValue(numericValue.toString())
      onChange(numericValue)
    }

    return (
      <Box width="100%">
        <Text textStyle="l-xs" color={required ? 'solid.700' : 'solid.500'} fontWeight={required ? '600' : undefined} mb={1}>
          {label}
          {required && <Text as="span" color="primary.100"> *</Text>}
        </Text>
        <InputGroup ref={ref}>
          <Input
            type="text"
            value={isFocused ? inputValue : (format ? format(value) : value.toString())}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            height="40px"
            color="solid.900"
            padding="10px 12px 10px 10px"
            gap="8px"
            borderRadius="8px"
            border="1px solid.500"
            bg={required ? 'primary.0' : undefined}
            borderColor={required ? 'primary.50' : undefined}
            _focus={required ? { borderColor: 'primary.100', bg: 'white' } : undefined}
          />
          <InputLeftElement>
            <Icon 
              as={ChevronDownIcon} 
              onClick={decrement} 
              cursor="pointer"
              color="solid.400" 
            />
          </InputLeftElement>
          <InputRightElement w="18px" h="100%" right="10px" mr="2px">
            <Flex w="18px" h="100%" flexDirection="column" justifyContent="center" alignItems="center">
              <Button onClick={increment} minWidth="18px" maxWidth="18px" height="10px" variant={'icon'} p={0}>
                <Icon width="18px" height="18px" viewBox="0 0 24 24" fill="none" color="solid.400">
                  <ChevronUpIcon />
                </Icon>
              </Button>
              <Button onClick={decrement} minWidth="18px" maxWidth="18px" height="10px" variant={'icon'} p={0} mt="2px">
                <Icon width="18px" height="18px" viewBox="0 0 24 24" fill="none" color="solid.400">
                  <ChevronDownIcon />
                </Icon>
              </Button>
            </Flex>
          </InputRightElement>
        </InputGroup>
      </Box>
    )
  }
)

export default CustomNumericInput